import React from 'react';
import {ToasterWithStatusAndDesriptionParams} from '../../context-providers/ToasterProvider/ToasterProvider';
import {contractAddresses} from '../../helpers/config/contracts.config';
import {getErrorMessage} from '../../helpers/error-message.helper';
import Singleton from '../../helpers/singleton';
import {BigNumber, constants} from 'ethers';

interface InjectedDependencies {
    publicClient: any;
    nrsContract: any;
    veNrsContract: any,
    nrsWithdrawalTimelockContract: any,
    userAddress: `0x${string}` | undefined;
    addToasterWithStatusAndDescription: (params: ToasterWithStatusAndDesriptionParams) => void;
    removeToaster: (id: string) => void;
    closeModal: () => void;
    signTypedData: (data: any) => Promise<string>;
}

@Singleton
export class DepositWithdrawalStakeService {

    private publicClient!: any;
    private nrsContract!: any;
    private veNrsContract!: any;
    private nrsWithdrawalTimelockContract!: any;
    private userAddress?: string;
    private MAX_AMOUNT = constants.MaxUint256;
    private showToaster!: (params: ToasterWithStatusAndDesriptionParams) => void;
    private removeToaster!: (id: string) => void;
    private closeModal!: () => void;
    private signTypedData!: (data: any) => Promise<string>;

    public injectDependencies(deps: InjectedDependencies) {
        this.publicClient = deps.publicClient;
        this.nrsContract = deps.nrsContract;
        this.userAddress = deps.userAddress;
        this.veNrsContract = deps.veNrsContract;
        this.nrsWithdrawalTimelockContract = deps.nrsWithdrawalTimelockContract;
        this.showToaster = deps.addToasterWithStatusAndDescription;
        this.removeToaster = deps.removeToaster;
        this.closeModal = deps.closeModal;
        this.signTypedData = deps.signTypedData;
    }

    public async withdraw(amountBN: BigNumber) {
        const toasterId = this.getToasterId('withdraw', amountBN.toString());
        if (!this.userAddress) {
            throw new Error('User address or signer is not defined');
        }

        const withdrawPendingDescription = this.getWithdrawToasterDescription();

        this.showToaster({
            id: toasterId,
            status: 'pending',
            title: 'Withdraw NRS',
            description: withdrawPendingDescription
        });
        try {
            const hash = await this.veNrsContract.write.withdraw([amountBN, this.userAddress, this.userAddress]);
            this.closeModal();
            await this.publicClient.waitForTransactionReceipt({ hash })

            this.removeToaster(toasterId);
            this.showToaster({
                id: toasterId,
                status: 'success',
                title: 'Withdraw NRS',
                description: withdrawPendingDescription,
                closeTime: 10000,
            })
        } catch (err: any) {
            this.showToaster({
                id: toasterId,
                status: 'error',
                title: 'Withdraw NRS',
                description: getErrorMessage(err),
                closeTime: 15000
            });
            throw err;
        }
    }

    public async claim(indices: number[]) {
        const toasterId = this.getToasterId('claim', Date.now().toString());
        if (!this.userAddress) {
            throw new Error('User address or signer is not defined');
        }

        this.showToaster({
            id: toasterId,
            status: 'pending',
            title: 'Claim NRS',
            description: ''
        });
        try {
            const sortedDescIndices = [...indices].sort((a, b) => b - a);
            const hash = await this.nrsWithdrawalTimelockContract.write.claim([sortedDescIndices]);
            this.closeModal();
            await this.publicClient.waitForTransactionReceipt({ hash })

            this.removeToaster(toasterId);
            this.showToaster({
                id: toasterId,
                status: 'success',
                title: 'Claim NRS',
                description: '',
                closeTime: 10000,
            })
        } catch (err: any) {
            console.log('err', err)
            this.showToaster({
                id: toasterId,
                status: 'error',
                title: 'Claim NRS',
                description: getErrorMessage(err),
                closeTime: 15000
            });
            throw err;
        }
    }

    public async approveAmount(amount: BigNumber) {
        const toasterId = this.getToasterId('approve', amount.toString());
        const approveDescription = 'Approve Nereus to use NRS for making deposits.';
        this.showToaster({
            id: toasterId,
            status: 'pending',
            title: 'Approve NRS',
            description: approveDescription
        });
        try {
            const hash = await this.nrsContract.write.approve([contractAddresses.polygon.veNrs, amount]);
            await this.publicClient.waitForTransactionReceipt({ hash })

            this.showToaster({
                id: toasterId,
                status: 'success',
                title: 'Approve NRS',
                description: approveDescription,
                closeTime: 10000,
            })
        } catch (error) {
            this.showToaster({
                id: toasterId,
                status: 'error',
                title: 'Approve NRS',
                description: getErrorMessage(error),
                closeTime: 10000,
            });
            throw error;
        }
    }

    public async deposit(amountBN: BigNumber) {
        if (!this.userAddress) {
            throw new Error('User address or signer is not defined');
        }
        const toasterId = this.getToasterId('deposit', amountBN.toString());
        const title = 'Staking NRS';
        this.showToaster({
            id: toasterId,
            status: 'pending',
            title,
            description: this.getDepositToasterDescription()
        });
        try {
            const hash = await this.veNrsContract.write.deposit([amountBN, this.userAddress]);
            this.closeModal();
            await this.publicClient.waitForTransactionReceipt({ hash })

            this.removeToaster(toasterId);
            this.showToaster({
                id: toasterId,
                status: 'success',
                title: 'Stake NRS',
                description: this.getDepositToasterDescription(),
                closeTime: 10000,
            })
        } catch (error) {
            this.showToaster({
                id: toasterId,
                status: 'error',
                title,
                description: getErrorMessage(error),
                closeTime: 10000,
            });
            throw error;
        }
    }

    private getToasterId(actionType: string, amount: string) {
        return JSON.stringify({
            actionType,
            amount,
            timestamp: Date.now()
        });
    }

    public getWithdrawToasterDescription() {
        return <><span className="nereus-color-main">Withdrawals will be available for claiming after a 30-day redemption period.</span> Please note, rewards will not accrue on the requested withdrawal amount during this period.</>
    }

    public getDepositToasterDescription() {
        return <>Your funds will be available on your balance shortly.</>
    }
}